export type TripInfoEntry = {
  prestId?: number | string
  familyId?: number
  details?: Record<string, unknown>
  tripInformation?: Record<string, unknown>
}

export function collectTripInfoLeaves(raw: unknown): TripInfoEntry[] {
  const leaves: TripInfoEntry[] = []
  const collect = (node: unknown) => {
    if (!node) return
    if (Array.isArray(node)) {
      node.forEach(collect)
      return
    }
    if (typeof node === 'object') {
      const obj = node as Record<string, unknown>
      if ('details' in obj || 'pricePerTraveller' in obj || 'familyId' in obj) {
        leaves.push(obj as TripInfoEntry)
        return
      }
      Object.values(obj).forEach(collect)
    }
  }
  collect(raw)
  return leaves
}

export function tripInfoPrestId(entry: TripInfoEntry): number | null {
  return tripInfoPrestIds(entry)[0] ?? null
}

function toPrestId(value: unknown): number | null {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : null
}

/** Cart prestId plus dual Iresa pair (outbound / inbound) on the leaf or details. */
export function tripInfoPrestIds(entry: TripInfoEntry): number[] {
  const details = (entry.details || entry.tripInformation) as Record<string, unknown> | undefined
  const extra = entry as TripInfoEntry & {
    prestIdOutbound?: unknown
    prestIdInbound?: unknown
  }
  const ids = [
    toPrestId(entry.prestId),
    toPrestId(details?.prestId),
    toPrestId(extra.prestIdOutbound),
    toPrestId(extra.prestIdInbound),
    toPrestId(details?.prestIdOutbound),
    toPrestId(details?.prestIdInbound),
  ].filter((id): id is number => id != null)
  return [...new Set(ids)]
}

function isStaleTransferMismatch(
  option: { prestId?: number | string | null; name?: string | null },
  details: Record<string, unknown> | undefined
): boolean {
  if (!details) return false
  const optionPrestId = Number(option.prestId)
  const detailsPrestIds = tripInfoPrestIds({ details, prestId: details.prestId as number | undefined })
  if (
    Number.isFinite(optionPrestId) &&
    optionPrestId > 0 &&
    detailsPrestIds.includes(optionPrestId)
  ) {
    return false
  }
  const detailsPrestId = Number(details.prestId)
  const transferType = details.transferType as string | undefined
  const nameLower = (option.name || '').toLowerCase()
  const isAutocarByName =
    nameLower.includes('autocar') ||
    nameLower.includes('bus') ||
    nameLower.includes('navette')
  const isTaxiByName =
    nameLower.includes('taxi') && !nameLower.includes('autocar')

  if (
    isAutocarByName &&
    transferType === 'TAXI' &&
    Number.isFinite(optionPrestId) &&
    optionPrestId > 0 &&
    detailsPrestId !== optionPrestId
  ) {
    return true
  }

  if (
    isTaxiByName &&
    !nameLower.includes('taxi partagé') &&
    transferType === 'BUS' &&
    Number.isFinite(optionPrestId) &&
    optionPrestId > 0 &&
    detailsPrestId !== optionPrestId
  ) {
    return true
  }

  return false
}

/** Same matching rules as call-center booking UI getTripInfo. */
export function resolveTripInfoForOption(
  option: {
    prestId?: number | string | null
    familyId?: number | null
    name?: string | null
  },
  rawTripInformation: unknown
): TripInfoEntry[] {
  if (rawTripInformation == null) return []

  const optionPrestId = Number(option.prestId)
  const hasPrestId = Number.isFinite(optionPrestId) && optionPrestId > 0
  const leaves = collectTripInfoLeaves(rawTripInformation)
  if (!leaves.length) return []

  const matchedByPrestId = hasPrestId
    ? leaves.filter((entry) => tripInfoPrestIds(entry).includes(optionPrestId))
    : []

  const filterByFamily = (entries: TripInfoEntry[]) =>
    option.familyId != null
      ? entries.filter((e) => e.familyId == null || e.familyId === option.familyId)
      : entries

  if (matchedByPrestId.length > 0) {
    return filterByFamily(matchedByPrestId)
  }

  if (leaves.length === 1) {
    const entry = leaves[0]
    const details = (entry.details || entry.tripInformation) as
      | Record<string, unknown>
      | undefined
    if (isStaleTransferMismatch(option, details)) {
      return []
    }
    return filterByFamily(leaves)
  }

  return []
}

const TRANSFER_FAMILY_ID = 78

function transferJourneyKey(
  option: {
    id?: number | null
    prestId?: number | string | null
    familyId?: number | null
    name?: string | null
    tripInformation?: unknown
  }
): string {
  const resolved = resolveTripInfoForOption(
    { prestId: option.prestId ?? option.id, familyId: option.familyId, name: option.name },
    option.tripInformation
  )
  const ids = resolved.length ? tripInfoPrestIds(resolved[0]) : []
  if (ids.length) return `ti-${[...ids].sort((a, b) => a - b).join('-')}`
  return `opt-${option.id ?? option.prestId ?? option.name ?? 'unknown'}`
}

/**
 * Dual prest: two Iresa TRANSFERS options, one tripInformation (aller+retour).
 * Show that journey once. Legacy single A/R option is unchanged.
 */
export function dedupeTransferOptionsForDisplay<
  T extends {
    id?: number | null
    familyId?: number | null
    nameCommercial?: string | null
    tripInformation?: unknown
  }
>(options: T[]): T[] {
  const seen = new Set<string>()
  const result: T[] = []
  for (const option of options) {
    if (option.familyId !== TRANSFER_FAMILY_ID) {
      result.push(option)
      continue
    }
    const key = transferJourneyKey({
      id: option.id,
      prestId: option.id,
      familyId: option.familyId,
      name: option.nameCommercial,
      tripInformation: option.tripInformation,
    })
    if (seen.has(key)) continue
    seen.add(key)
    result.push(option)
  }
  return result
}

export function transferTypeFromOptionName(
  optionName: string | null | undefined,
  labels: { bus: string; taxi: string; funicular: string }
): string | null {
  const nameLower = (optionName || '').toLowerCase()
  if (nameLower.includes('taxi partagé')) return labels.taxi
  if (
    nameLower.includes('autocar') ||
    nameLower.includes('bus') ||
    nameLower.includes('navette')
  ) {
    return labels.bus
  }
  if (nameLower.includes('taxi') && !nameLower.includes('autocar')) {
    return labels.taxi
  }
  if (nameLower.includes('funiculaire') || nameLower.includes('funicular')) {
    return labels.funicular
  }
  return null
}
